import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, Toolbar } from '@/components/inputs';
import { ORDER_STATUSES, ORDER_STATUS_STYLES } from '@/shared/constants';
import { ORDER_LIST_FIELDS } from '@/shared/fields';
import { BANSHEE_COMPARE_PATH, BANSHEE_PATH, PLANS, money, orderTotal, planSummary, timeLeft } from '@/shared/pricing';
import { paged } from '@/shared/data';
import { CODE_PATTERN, CODE_PREFIX, asOrderCode } from '@/shared/orderCode';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';
import { useT } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';
import { B } from '@/shared/locales/account2/keys';
import { C } from '@/shared/locales/checkout/keys';
import { planSummaryT, timeLeftT } from '@/shared/accountText';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/account/orders', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'Package', label: 'Orders', section: 'My account', profiles: ['Customer_c'], order: 20 };

const PAGE_SIZE = 10;

function FindOrder() {
  const { t } = useT();
  const navigate = useNavigate();
  const [code, setCode] = useState(CODE_PREFIX);
  const [checking, setChecking] = useState(false);
  const valid = CODE_PATTERN.test(code);

  async function submit(e) {
    e.preventDefault();
    if (!valid) {
      toast.error(t(B.codeShape));
      return;
    }
    setChecking(true);
    try {
      const res = await sdk.table('orders_c').select(['Name']).where(eq('Name', code)).limit(1).fetch();
      const found = res?.data?.[0];
      if (found) navigate(`/account/orders/${found.Id}`);
      else toast.error(t(B.noOrderCode, { code }), { description: t(B.checkReceipt) });
    } catch {
      toast.error(t(B.cantCheck));
    } finally {
      setChecking(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex w-full items-center gap-2 sm:w-auto">
      <label htmlFor="find-order" className="sr-only">
        {t(C.orderNumber)}
      </label>
      <Input
        id="find-order"
        value={code}
        onChange={(e) => setCode(asOrderCode(e.target.value))}
        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
        placeholder="GC-7K2QXM"
        autoComplete="off"
        spellCheck={false}
        className="w-full font-mono uppercase sm:w-40"
      />
      <Button type="submit" variant="outline" disabled={checking || !valid}>
        <ApperIcon name={checking ? 'Loader2' : 'Search'} size={16} className={checking ? 'animate-spin' : undefined} />
        {t(C.checkOrder)}
      </Button>
    </form>
  );
}

export default function Orders() {
  const { t } = useT();
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [status]);

  const { data, loading, error, run } = useFetch(() => {
    let q = sdk.table('orders_c').select(ORDER_LIST_FIELDS);
    if (status !== 'all') q = q.where(eq('status_c', status));
    return q.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [status, page]);

  const list = data?.rows ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={t('My account')}
        title={t(A.navOrders)}
        description={t(B.ordersDesc)}
        actions={
          <Button asChild>
            <Link to="/checkout">
              <ApperIcon name="Plus" size={16} />
              {t(A.newOrder)}
            </Link>
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ApperIcon name="Lock" size={15} />
          </span>
          <div>
            <p className="text-sm font-medium">{t(B.privateN, { name: PLANS.Banshee.name })}</p>
            <p className="text-xs text-muted-foreground">
              {t(B.inviteOnly)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={BANSHEE_PATH}>{t(B.seeN, { name: PLANS.Banshee.name })}</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to={BANSHEE_COMPARE_PATH}>
              <ApperIcon name="Scale" size={15} />
              {t(B.compareThree)}
            </Link>
          </Button>
        </div>
      </div>

      <Toolbar>
        <FindOrder />
        <FilterSelect value={status} onChange={setStatus} values={ORDER_STATUSES} allLabel={B.allStatuses} />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Package',
          title: status === 'all' ? t(A.noOrders) : t(B.noStatus),
          description: status === 'all' ? t(B.firstOrder) : t(B.tryStatus),
          action: status === 'all' ? () => navigate('/checkout') : undefined,
          actionLabel: status === 'all' ? t(A.startOrder) : undefined,
        }}
      >
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(B.colOrder)}</TableHead>
                <TableHead className="hidden md:table-cell">{t(B.colPackage)}</TableHead>
                <TableHead>{t(B.colStatus)}</TableHead>
                <TableHead className="hidden lg:table-cell">{t(B.colOrdered)}</TableHead>
                <TableHead className="hidden md:table-cell">{t(B.colEnds)}</TableHead>
                <TableHead className="text-end">{t(C.total)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((o) => (
                <TableRow key={o.Id}>
                  <TableCell>
                    <Link to={`/account/orders/${o.Id}`} className="font-mono text-primary hover:underline">
                      {o.Name}
                    </Link>
                    <p className="text-xs text-muted-foreground md:hidden">{planSummaryT(o, t)}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{planSummaryT(o, t)}</TableCell>
                  <TableCell>
                    <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} />
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground lg:table-cell">
                    {formatLocalDate(o.CreatedOn, DATE_FORMATS.SHORT)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {o.ends_at_c ? (
                      <span>
                        {formatLocalDate(o.ends_at_c, DATE_FORMATS.SHORT)}
                        <span className="block text-xs text-muted-foreground">{timeLeftT(o.ends_at_c, t)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">{money(orderTotal(o))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
    </div>
  );
}
